import { useEffect, useState } from 'react'
import { useStartupContext } from '../contexts/startup-guard'
import { useStartupMatchesQuery } from './api'

/**
 * Single source of truth for "is the founder workspace unlocked?" — used by
 * both the sidebar (to disable nav items) and the per-route guard (to block
 * direct URL access).
 *
 * `ready` is the best-guess for the current paint, `isResolved` is whether
 * we have an authoritative answer or are still relying on a localStorage
 * hint. The hint avoids a flash of "disabled" nav / redirect on every
 * refresh — see `sf-has-matches-${startupId}` cache.
 */
export interface WorkspaceReadyState {
  ready: boolean
  isResolved: boolean
}

export function useWorkspaceReady(): WorkspaceReadyState {
  const { startup, pitchDeckStatus, investorMatchesUnlocked } = useStartupContext()

  const cacheKey = `sf-has-matches-${startup.id}`
  const [cachedHasMatches, setCachedHasMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(cacheKey) === '1'
  })

  const matchesQuery = useStartupMatchesQuery(startup.id, 50)
  const liveData = matchesQuery.data?.data
  const liveHasMatches = liveData
    ? liveData.matches.filter((m) => investorMatchesUnlocked || m.source !== 'paid_eval').length > 0
    : null

  useEffect(() => {
    if (liveHasMatches === null) return
    if (typeof window !== 'undefined') {
      localStorage.setItem(cacheKey, liveHasMatches ? '1' : '0')
    }
    setCachedHasMatches(liveHasMatches)
  }, [liveHasMatches, cacheKey])

  const deckReady = pitchDeckStatus === 'READY'
  // pitchDeckStatus comes from the startup-context blocking load and is
  // authoritative the moment we render. Matches need network — fall back
  // to the cached hint until the live query confirms.
  if (!deckReady) {
    return { ready: false, isResolved: true }
  }
  if (liveHasMatches !== null) {
    return { ready: liveHasMatches, isResolved: true }
  }
  return { ready: cachedHasMatches, isResolved: false }
}
